import { useParams } from 'next/navigation';
import { memo, useEffect, useRef } from 'react';

import { useDialogs } from '~entities/dialogs';
import { annals } from '~features/annals/hooks';
import { NSFWContactsNotice } from '~features/nsfw';
import { useSession } from '~hooks/useSession';
import { gridApi } from '~services/api/grid';
import { Loader } from '~shared/ui/atoms';

import { ContactsItem } from '../ContactsItem';
import { ContactsSlider } from '../ContactsSlider';
import { Body, Container, ListWrapper } from './styles';

type Props = {
	onAboveLevelBotClick: (botClientId: number) => void;
};

export const Contacts = memo(({ onAboveLevelBotClick }: Props) => {
	const { recipientId = '' } = useParams<{ recipientId?: string }>();

	const { bots, isLoading, nsfwCount } = useDialogs();
	const { isDemoUser, isSafeMode, userId } = useSession();
	const [triggerBotClicked] = gridApi.useBotClickedMutation();

	const annalsChatClick = annals.chat.useChatClickFromList();
	const annalsContactsShowed = annals.chat.useChatContactsShowed();

	const handleActiveBotClick = (botClientId: number, botName: string) => {
		if (!isDemoUser) {
			void triggerBotClicked({
				userId,
				botId: botClientId,
			});
		}
		void annalsChatClick({
			bot_UserId: botClientId,
			bot_name: botName,
			bot_disabled: false,
		});
	};

	const handleDisabledBotClick = (botClientId: number, botName: string) => {
		onAboveLevelBotClick(botClientId);

		void annalsChatClick({
			bot_UserId: botClientId,
			bot_name: botName,
			bot_disabled: true,
		});
	};

	const activeBots = bots.filter(
		(bot) => !bot.disabledByLevel && bot.lastMessage
	);
	const disabledBots = bots.filter((bot) => bot.disabledByLevel);

	const scrollRef = useRef<HTMLDivElement>(null);
	const latestChatBotId = useRef<number>(null);

	useEffect(() => {
		if (!isLoading && userId) {
			void annalsContactsShowed({
				chats_order: activeBots.map(({ clientId }) => clientId),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, userId]);

	useEffect(() => {
		if (
			activeBots.length > 0 &&
			latestChatBotId.current !== activeBots[0].clientId
		) {
			if (latestChatBotId.current) {
				scrollRef.current?.scrollTo(0, 0);
			}
			latestChatBotId.current = activeBots[0].clientId;
		}
	}, [activeBots]);

	return (
		<Container>
			{isLoading && <Loader />}

			<Body ref={scrollRef}>
				<ListWrapper>
					{activeBots.map((bot) => (
						<ContactsItem
							key={bot.clientId}
							bot={bot}
							isActive={+recipientId === bot.clientId}
							onClick={
								+recipientId !== bot.clientId
									? () => handleActiveBotClick(bot.clientId, bot.name)
									: undefined
							}
						/>
					))}
					{isSafeMode && nsfwCount > 0 && (
						<NSFWContactsNotice counter={nsfwCount} />
					)}
				</ListWrapper>
			</Body>

			{!isDemoUser && disabledBots.length > 0 && (
				<ContactsSlider bots={disabledBots} onClick={handleDisabledBotClick} />
			)}
		</Container>
	);
});
Contacts.displayName = 'Contacts';
